import React, { useEffect, useState } from 'react';
import SelectField from '../components/SelectField';
import { FaChevronRight, FaEraser, FaFileAlt, FaFilter, FaSearch } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { InlineSpinner } from '../components/LoadingState';
import { fetchOvrLogs } from '../api/api';
import RefreshButton from '../components/RefreshButton';
import LoadingState from '../components/LoadingState';
import { parseCallFilename, formatTimestamp, detectChanges } from '../common/auditFormat';
import DateTimeInput from '../components/DateTimeInput';

const AuditView = () => {
  const navigate = useNavigate();
  const [allLogs, setAllLogs] = useState({ items: [] }); // All loaded logs
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState(null);
  const [hasMoreData, setHasMoreData] = useState(true);
  
  // Search states for querying specific items
  const [searchFilename, setSearchFilename] = useState('');
  const [searchFilenameValue, setSearchFilenameValue] = useState('');
  const [startTimestamp, setStartTimestamp] = useState('');
  const [endTimestamp, setEndTimestamp] = useState('');
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchResults, setSearchResults] = useState({ items: [] });
  
  // UI filter states (for filtering loaded data)
  const [filters, setFilters] = useState({
    username: '',
    campaign: '',
    template: ''
  });
  
  // Get unique values for filter dropdowns
  const getUniqueValues = (key) => {
    const sourceItems = (isSearchMode ? searchResults?.items : allLogs?.items) || [];
    const values = sourceItems
      .map(item => item[key])
      .filter(value => value && value !== '')
      .filter((value, index, self) => self.indexOf(value) === index)
      .sort();
    return values;
  };
  
  // Get filtered items based on current mode and UI filters
  const getFilteredItems = () => {
    const sourceItems = (isSearchMode ? searchResults?.items : allLogs?.items) || [];

    return sourceItems.filter(item => {
      const matchesUsername = !filters.username || 
        (item.email && item.email.toLowerCase().includes(filters.username.toLowerCase()));
      
      const matchesCampaign = !filters.campaign || 
        (item.campaign && item.campaign === filters.campaign);
      
      const matchesTemplate = !filters.template || 
        (item.template && item.template === filters.template);
      
      return matchesUsername && matchesCampaign && matchesTemplate;
    });
  };

  const loadMoreLogs = async (isLoadMore = false, lastKey = null) => {
    try {
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      // Ensure lastKey is properly stringified if it's an object
      let lastKeyParam = null;
      if (lastKey) {
        lastKeyParam = typeof lastKey === 'object' ? JSON.stringify(lastKey) : lastKey;
      }

      // Call with standard parameters - no filename for regular load
      const res = await fetchOvrLogs(lastKeyParam, 20, null, null, null);
        
      console.log('Logs : ', res);
      
      if (isLoadMore) {
        // Append new items to existing ones
        setAllLogs(prev => ({
          ...res,
          items: [...(prev.items || []), ...(res.items || [])]
        }));
      } else {
        // Replace with new data (initial load)
        setAllLogs(res);
      }

      // Update pagination state
      setLastEvaluatedKey(res.lastEvaluatedKey || null);
      setHasMoreData(!!res.lastEvaluatedKey);

    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const searchByFilename = async (filename, startTime = null, endTime = null) => {
    try {
      setLoading(true);
      setIsSearchMode(true);
      
      // Convert datetime-local values to UTC ISO strings so they compare
      // correctly against the stored UTC ISO timestamps server-side.
      const startIso = startTime ? new Date(startTime).toISOString() : null;
      const endIso = endTime ? new Date(endTime).toISOString() : null;

      // Use the fetchOvrLogs function with all parameters
      const res = await fetchOvrLogs(null, 100, filename, startIso, endIso);
      console.log('Search results: ', res);
      
      setSearchResults(res);
      
    } catch (error) {
      console.error('Error searching logs:', error);
      setSearchResults({ items: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMoreLogs();
  }, []);

  const handleLoadMore = () => {
    if (!loadingMore && hasMoreData && !isSearchMode) {
      loadMoreLogs(true, lastEvaluatedKey);
    }
  };

  const handleFilenameSearch = (e) => {
    e.preventDefault();
    if (searchFilenameValue.trim()) {
      searchByFilename(
        searchFilenameValue.trim(), 
        startTimestamp || null, 
        endTimestamp || null
      );
      setSearchFilename(searchFilenameValue.trim());
    }
  };

  const clearSearch = () => {
    setSearchFilename('');
    setSearchFilenameValue('');
    setStartTimestamp('');
    setEndTimestamp('');
    setIsSearchMode(false);
    setSearchResults({ items: [] });
    // Reload all logs after clearing search
    loadMoreLogs();
  };

  const clearFilters = () => {
    setFilters({
      username: '',
      campaign: '',
      template: ''
    });
  };

  const hasActiveFilters = filters.username || filters.campaign || filters.template;
  const filteredItems = getFilteredItems();

  const ItemCard = ({ item, onClick }) => {
    const totalChanges = Object.keys(item.old || {}).reduce((count, key) => {
      const changes = detectChanges(item.old[key], item.new?.[key] || '');
      return count + changes.length;
    }, 0);

    const nameFormat = (filename) => {
      if (filename && filename.length >= 60) {
        return filename.slice(0, 60) + '...';
      }
      return filename;
    };

    const { agent, callTime, assignee, jobId } = parseCallFilename(item.filename || '');

    return (
      <div 
        className="group bg-white rounded-xl shadow-sm hover:shadow-md transition-all cursor-pointer border border-gray-200 hover:border-cloudza-300"
        onClick={onClick}
      >
        <div className="p-6">
          <div className="flex flex-wrap items-center justify-between gap-y-2 mb-4">
            <div className="flex items-center space-x-4 min-w-0">
              <div className="flex-shrink-0">
                {agent ? (
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cloudza-500 to-cloudza-700 text-white text-base font-bold flex items-center justify-center uppercase shadow-sm">
                    {agent.trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join('')}
                  </div>
                ) : (
                  <div className="w-12 h-12 bg-cloudza-50 rounded-xl flex items-center justify-center">
                    <FaFileAlt className="w-5 h-5 text-cloudza-600" />
                  </div>
                )}
              </div>
              
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-gray-800 truncate">
                  {agent ? (
                    <>{agent}{callTime && <span className="font-normal text-gray-500"> · {formatTimestamp(callTime)}</span>}</>
                  ) : assignee ? (
                    <>Assigned to {assignee}<span className="font-normal text-gray-500"> · Job {jobId}</span></>
                  ) : (
                    nameFormat(item.filename)
                  )}
                </h3>
                <p className="text-sm text-gray-600 truncate">{item.email}</p>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {item.campaign && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-cloudza-50 text-cloudza-700 border border-cloudza-100 text-xs font-medium">
                      {item.campaign}
                    </span>
                  )}
                  {item.template && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-coral-50 text-coral-700 border border-coral-200 text-xs font-medium">
                      {item.template}
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            <div className="text-right flex-shrink-0">
              <p className="text-sm text-gray-500">{formatTimestamp(item.updatedAt)}</p>
              {totalChanges > 0 && (
                <span className="status-badge status-medium mt-1">
                  {totalChanges} change{totalChanges === 1 ? '' : 's'}
                </span>
              )}
            </div>
          </div>
          
          {item.old && (
            <div className="flex items-start gap-2 text-sm text-gray-600">
              <span className="text-xs text-gray-400 pt-1 flex-shrink-0">Fields</span>
              <div className="flex flex-wrap gap-1.5 min-w-0">
                {/* Keys here are whole question texts, so no `capitalize` —
                    it Title-Cases every word and makes them unreadable. Some
                    keys are short reference codes and others are full
                    questions; without a capped width + truncate, the long
                    ones wrap onto several lines and stop reading as tags at
                    all, so every badge is forced to the same single-line
                    shape regardless of its content length. */}
                {Object.keys(item.old).map(key => (
                  <span
                    key={key}
                    title={key}
                    className="inline-block max-w-[220px] truncate align-middle bg-gray-50 border border-gray-200 text-gray-600 px-2 py-1 rounded-lg text-xs"
                  >
                    {key}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          <div className="mt-4 flex items-center gap-1 text-cloudza-600 group-hover:text-cloudza-800 text-sm font-medium transition-colors">
            <span>View changes</span>
            <FaChevronRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
          </div>
        </div>
      </div>
    );
  };

  // Compact spinner for the inline "load more" state.
  const LoadingSpinner = () => (
    <div className="flex justify-center items-center py-8">
      <InlineSpinner className="!h-8 !w-8" />
    </div>
  );

  return (
    // No background or min-height of its own — the page sits on the app's
    // canvas like every other route. It previously painted its own gray-100
    // panel, which read as a lighter block against the surrounding surface.
    <div>
      {loading && !isSearchMode && (allLogs?.items?.length || 0) === 0 ? (
        <LoadingState label="Loading override logs" />
      ) : (
        <div className="space-y-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Audit</h1>
              <p className="text-gray-500 text-sm mt-1">Override history across campaigns and scorecards</p>
            </div>
            <RefreshButton onRefresh={() => (isSearchMode ? searchByFilename(searchFilename, startTimestamp || null, endTimestamp || null) : loadMoreLogs())} />
          </div>
          
          {/* Search and Filter Section */}
          <div className="card" data-tour="audit-search">
            {/* Query Specific Items Section */}
            <div className="mb-6">
              <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
                <FaSearch className="text-gray-400 text-xs" />
                Query Specific Item
              </h3>
              <form onSubmit={handleFilenameSearch}>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-1.5">
                      Filename <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Enter exact filename..."
                      value={searchFilenameValue}
                      onChange={(e) => setSearchFilenameValue(e.target.value)}
                      className="input-field"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-1.5">
                        Start Timestamp
                      </label>
                      <DateTimeInput
                        mode="datetime"
                        value={startTimestamp}
                        onChange={(e) => setStartTimestamp(e.target.value)}
                        className="input-field w-full"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-1.5">
                        End Timestamp
                      </label>
                      <DateTimeInput
                        mode="datetime"
                        value={endTimestamp}
                        onChange={(e) => setEndTimestamp(e.target.value)}
                        className="input-field w-full"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-2 mt-4">
                  <button
                    type="submit"
                    disabled={loading || !searchFilenameValue.trim()}
                    className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <FaSearch />
                    {loading ? 'Searching...' : 'Query Item'}
                  </button>
                  
                  {isSearchMode && (
                    <button
                      type="button"
                      onClick={clearSearch}
                      className="btn-outline"
                    >
                      <FaEraser />
                      Clear Query
                    </button>
                  )}
                </div>
              </form>
            </div>
            
            {/* UI Filter Section */}
            <div className="border-t border-gray-100 pt-6">
              <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
                <FaFilter className="text-gray-400 text-xs" />
                Filter Loaded Items
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-1.5">Username (Email)</label>
                  <input
                    type="text"
                    placeholder="Filter by email..."
                    value={filters.username}
                    onChange={(e) => setFilters({...filters, username: e.target.value})}
                    className="input-field w-full"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-1.5">Campaign</label>
                  <SelectField
                    value={filters.campaign}
                    onChange={(e) => setFilters({...filters, campaign: e.target.value})}
                    className="select-field w-full"
                  >
                    <option value="">All Campaigns</option>
                    {getUniqueValues('campaign').map(campaign => (
                      <option key={campaign} value={campaign}>{campaign}</option>
                    ))}
                  </SelectField>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-1.5">Template</label>
                  <SelectField
                    value={filters.template}
                    onChange={(e) => setFilters({...filters, template: e.target.value})}
                    className="select-field w-full"
                  >
                    <option value="">All Templates</option>
                    {getUniqueValues('template').map(template => (
                      <option key={template} value={template}>{template}</option>
                    ))}
                  </SelectField>
                </div>
              </div>
              
              {hasActiveFilters && (
                <div className="mt-4">
                  <button
                    onClick={clearFilters}
                    className="btn-secondary"
                  >
                    <FaEraser />
                    Clear All Filters
                  </button>
                </div>
              )}
            </div>
          </div>
          
          {/* Status Information */}
          <div className="mb-4 flex flex-wrap gap-4 text-sm">
            {isSearchMode ? (
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-cloudza-50 text-cloudza-700 border border-cloudza-100 text-xs font-semibold max-w-md truncate" title={searchFilename}>
                  Query: {searchFilename}
                </span>
                <span className="text-xs text-gray-400">{filteredItems.length} item{filteredItems.length === 1 ? '' : 's'}</span>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <h2 className="text-sm font-bold text-gray-900">All Logs</h2>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-cloudza-50 text-cloudza-700 text-xs font-semibold">
                  {filteredItems.length} of {(allLogs?.items?.length || 0)}
                </span>
              </div>
            )}
            
            {/* Active Filters Display */}
            {filters.username && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-coral-50 text-coral-700 border border-coral-200 text-xs font-medium">
                Email: {filters.username}
              </span>
            )}
            {filters.campaign && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-coral-50 text-coral-700 border border-coral-200 text-xs font-medium">
                Campaign: {filters.campaign}
              </span>
            )}
            {filters.template && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-coral-50 text-coral-700 border border-coral-200 text-xs font-medium">
                Template: {filters.template}
              </span>
            )}
          </div>
          
          {/* Loading indicator for search */}
          {loading && isSearchMode && (
            <div className="text-center py-8">
              <LoadingSpinner />
              <p className="text-gray-600 mt-2">Querying database...</p>
            </div>
          )}
          
          {/* Items List */}
          <div className="space-y-4">
            {filteredItems.map((item, index) => (
              <ItemCard
                key={`${item.filename}-${item.updatedAt}-${index}`}
                item={item}
                onClick={() =>
                  navigate(
                    `/override-logs/detail?filename=${encodeURIComponent(item.filename)}&updatedAt=${encodeURIComponent(item.updatedAt)}`,
                    { state: { item } }
                  )
                }
              />
            ))}
          </div>
          
          {/* Load More Button - only show when not in search mode */}
          {!isSearchMode && hasMoreData && (
            <div className="flex justify-center mt-8">
              <button
                onClick={handleLoadMore}
                disabled={loadingMore}
                className="bg-cloudza-600 hover:bg-cloudza-700 disabled:bg-cloudza-300 text-white px-5 py-2.5 rounded-full text-sm font-semibold transition-colors flex items-center space-x-2"
              >
                {loadingMore ? (
                  <>
                    <InlineSpinner light />
                    <span>Loading...</span>
                  </>
                ) : (
                  <>
                    <span>Load More</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </>
                )}
              </button>
            </div>
          )}
          
          {/* No more data message */}
          {!isSearchMode && !hasMoreData && (allLogs?.items?.length || 0) > 0 && (
            <div className="text-center mt-8 py-4">
              <p className="text-gray-500">No more logs to load</p>
            </div>
          )}
          
          {/* No results message */}
          {filteredItems.length === 0 && !loading && (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-4">
                <svg className="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-600 mb-2">No results found</h3>
              <p className="text-gray-500">
                {isSearchMode 
                  ? "No logs found for the specified filename and timestamp range." 
                  : hasActiveFilters 
                    ? "No logs match the current filters."
                    : "No logs available."
                }
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AuditView;